import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * HorizontalScrollContainer - Reusable wrapper for horizontal scrolling with fade edges
 *
 * Detects scroll position and applies CSS classes for left/right fade indicators.
 * Used by AgentDataTable and markdown table rendering in AgentAIMessage.
 *
 * Features:
 * - Automatic scroll position detection
 * - Dynamic CSS classes for fade edge visibility (scrolled-left, scrolled-right)
 * - Cleanup of event listeners on unmount
 * - Re-checks scroll state when children change
 *
 * @param {React.ReactNode} children - Content to wrap (typically a table)
 * @param {string} className - Additional CSS classes for the wrapper
 * @param {string} containerClassName - Additional CSS classes for the scroll container
 *
 * @example
 * <HorizontalScrollContainer className="my-table-wrapper">
 *   <table>...</table>
 * </HorizontalScrollContainer>
 */
const HorizontalScrollContainer = ({ children, className = '', containerClassName = '' }) => {
  const containerRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    isScrolledLeft: false,
    isScrolledRight: false
  });

  /**
   * Detect scroll position and update state for fade edge visibility
   * - isScrolledLeft: true when scrolled away from left edge (show left fade)
   * - isScrolledRight: true when not at right edge (show right fade)
   */
  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    setScrollState({
      isScrolledLeft: scrollLeft > 0, // Show left fade when scrolled away from left edge
      isScrolledRight: scrollLeft < maxScrollLeft // Show right fade when not at right edge
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Initial check on mount
    handleScroll();

    // Add scroll listener for fade edge detection only
    container.addEventListener('scroll', handleScroll);

    // Note: No custom wheel event handler.
    // Let browser handle all scrolling natively - this ensures vertical page
    // scrolling works when mouse is over the table. The browser's native
    // handling of overflow-x:auto handles horizontal scrolling correctly.

    // Cleanup on unmount
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [children]); // Re-run when children change (e.g., table data updates)

  const wrapperClassName = [
    'horizontal-scroll-wrapper',
    className,
    scrollState.isScrolledLeft ? 'scrolled-left' : '',
    scrollState.isScrolledRight ? 'scrolled-right' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClassName}>
      <div className={`horizontal-scroll-container ${containerClassName}`} ref={containerRef}>
        {children}
      </div>
    </div>
  );
};

HorizontalScrollContainer.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  containerClassName: PropTypes.string
};

export default HorizontalScrollContainer;
